// frontend/src/components/KybDocBlock.tsx
import React from "react";
import { DOC_LABELS, type KybDoc, type KybDocType } from "@/lib/domains/kybDocs";

function badge(status?: KybDoc["status"]) {
  switch (status) {
    case "approved":
      return { text: "APPROVED", cls: "bg-green-100 text-green-800 border-green-300" };
    case "pending":
      return { text: "PENDING", cls: "bg-yellow-100 text-yellow-800 border-yellow-300" };
    case "rejected":
      return { text: "REJECTED", cls: "bg-red-100 text-red-800 border-red-300" };
    default:
      return { text: "MISSING", cls: "bg-gray-100 text-gray-700 border-gray-300" };
  }
}

function acceptFor(type: KybDocType) {
  if (type === "certificate" || type === "articles") return ".pdf";
  return ".pdf,.jpg,.jpeg,.png";
}

export default function KybDocBlock({
  docType,
  doc,
  allowUpload,
  onPick,
}: {
  docType: KybDocType;
  doc?: KybDoc | null;
  allowUpload: boolean;
  onPick?: (type: KybDocType, file: File | null) => void;
}) {
  const label = DOC_LABELS[docType];

  // Treat absence as "missing" for UI
  const hasDoc = !!doc && (doc.url || doc.file_path || doc.id || doc.filename);
  const status: KybDoc["status"] = hasDoc ? (doc!.status ?? "pending") : "missing";
  const b = badge(status);
  const url = doc?.url ?? (doc?.file_path ?? null);

  return (
    <div className="mb-4 border rounded p-4 bg-gray-50">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="font-medium">{label}</div>
        <div className={`text-xs px-3 py-1.5 rounded border ${b.cls}`}>{b.text}</div>
      </div>

      <div className="text-sm mt-2">
        {hasDoc && url ? (
          <>
            <a href={url} target="_blank" className="text-blue-700 hover:underline" rel="noreferrer">
              View current document
            </a>
            {doc?.filename && <span className="text-gray-500"> — {doc.filename}</span>}
          </>
        ) : (
          <span className="text-gray-500 italic">No file uploaded yet</span>
        )}
      </div>

      {status === "rejected" && doc?.review_notes && (
        <div className="text-xs text-red-700 mt-2">Reason: {doc.review_notes}</div>
      )}

      <div className="mt-3">
        <input
          type="file"
          accept={acceptFor(docType)}
          disabled={!allowUpload}
          className={`block w-full ${allowUpload ? "" : "opacity-50 cursor-not-allowed"}`}
          onChange={(e) => {
            const f = e.target.files?.[0] ?? null;
            onPick?.(docType, f);
          }}
        />

        {/* Helper note logic:
            - If read-only and approved: show locked message
            - If read-only and a doc exists AND is pending: show "Awaiting review"
            - If missing: show nothing extra (we already show "No file uploaded yet")
        */}
        {!allowUpload && status === "approved" && (
          <p className="text-xs text-gray-500 mt-1">
            This document is approved and cannot be changed.
          </p>
        )}
        {!allowUpload && hasDoc && status === "pending" && (
          <p className="text-xs text-gray-500 mt-1">
            Awaiting review — please wait for the decision.
          </p>
        )}
      </div>
    </div>
  );
}
